import { useCallback, useState } from 'react';
import {
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { FormField, FormLayout } from '../../types/form';
import { updateFieldOrder, moveFieldToSection, removeFieldFromLayout } from './fieldUtils';

// ============================================================================
// Interfaces
// ============================================================================
interface DndConfig {
  layout: FormLayout['layout_config'];
  setLayout: React.Dispatch<React.SetStateAction<FormLayout['layout_config']>>;
  availableFields: FormField[];
  setAvailableFields: React.Dispatch<React.SetStateAction<FormField[]>>;
}

// ============================================================================
// Hook
// ============================================================================
export const useFormLayoutDnd = ({
  layout,
  setLayout,
  availableFields,
  setAvailableFields
}: DndConfig) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const findContainer = (id: string): string | null => {
    if (id.startsWith('available-')) return 'available';
    if (id.startsWith('section-')) return id.replace('section-', '');
    const field = layout.fields.find(f => f.id === id);
    return field?.sectionId ?? null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id.toString());
  };

  const handleDragOver = (event: DragOverEvent) => {
    // Optional: highlight or preview logic
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const activeIdStr = event.active.id.toString();
    const overIdStr = event.over?.id.toString();

    const from = findContainer(activeIdStr);
    const to = overIdStr ? findContainer(overIdStr) : null;

    if (from === 'available' && to && to !== 'available') {
      // Drop new field from Available into layout
      const fieldName = activeIdStr.replace('available-', '');
      const field = availableFields.find(f => f.field_name === fieldName);
      if (!field) return;

      moveFieldToSection({
        layout,
        setLayout,
        sectionId: to,
        field
      });
      setAvailableFields(prev => prev.filter(f => f.field_name !== fieldName));
    }

    if (from && from !== 'available' && to && to !== 'available' && overIdStr) {
      updateFieldOrder({ layout, setLayout, from, to, activeId: activeIdStr, overId: overIdStr });
    }

    if (to === 'available') {
      removeFieldFromLayout({ layout, setLayout, availableFields, setAvailableFields, activeId: activeIdStr });
    }

    setActiveId(null);
  };

  const collisionDetection = useCallback((args: Parameters<typeof pointerWithin>[0]) => {
    return rectIntersection(args);
  }, []);

  return {
    sensors,
    activeId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    collisionDetection
  };
};
